import clsx from 'clsx';
import { Check, ChevronLeft, ChevronRight, GraduationCap, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useStore, type View } from '../store/useStore';

/**
 * Guided first run.
 *
 * Steps advance when you *do* the thing, not when you click Next — each one
 * carries a `done` predicate, polled while the tour is open. That keeps it
 * honest: it can't claim you've imported a loop when you haven't. Next is still
 * there for reading ahead, and Back pauses the auto-advance so it doesn't drag
 * you forward again while you re-read something.
 */

interface TourStep {
  title: string;
  body: string;
  /** Element to ring, as a `data-tour` value. */
  anchor?: string;
  /**
   * View this step's UI lives on. Switched to when the step opens — the grid
   * and the preset palette don't exist on the chop screen, so without this the
   * tour cheerfully explains how to drag steps while showing you a waveform.
   */
  view?: View;
  /** True once the user has actually done it. */
  done?: () => boolean;
  /** Shown instead of the anchor ring when there's nothing to point at. */
  hint?: string;
}

const state = () => useStore.getState();

const STEPS: TourStep[] = [
  {
    title: 'Let’s chop a break',
    body: 'Six steps: pull a loop out of a track, chop it to a grid, then wreck it in ways that still land on the beat. You can leave at any point — the tour follows what you actually do.',
  },
  {
    title: 'Open the loop finder',
    body: 'This is the usual way in: load a whole song and let Happy Chopper lock a bar grid across it, so you can lift a clean 4 or 8 bars out of the middle.',
    anchor: 'find-loops',
    // Satisfied by the library route too, so nobody gets stuck waiting on a
    // modal they've decided not to open.
    done: () => !!document.querySelector('[data-modal-open]') || state().project.sources.length > 0,
  },
  {
    title: 'Load a track and pick your bars',
    body: 'Choose a file, then set the loop length and drag the highlighted region along the waveform. The amber marks underneath are windows that repeat strongly — usually the cleanest loops. Space previews it.',
    anchor: 'finder',
    hint: 'No track handy? Close this and use the breaks library instead — the tour will catch up.',
    done: () => state().project.sources.length > 0,
  },
  {
    title: 'Hit play',
    body: 'Your loop landed on a track, chopped into slices, with a 1:1 pattern so it plays back exactly as it came in. Start the transport and let it run while you work — everything from here is audible immediately.',
    anchor: 'transport',
    done: () => state().playing,
  },
  {
    title: 'Rearrange the slices',
    body: 'Drag any step onto another to reorder it. Alt-drag copies a slice, right-click reverses one, double-click mutes it. This is the whole idea — the pattern holds slice numbers, not audio.',
    anchor: 'pattern-grid',
    view: 'sequence',
    done: () => {
      const s = state();
      const track = s.project.tracks.find((t) => t.id === s.selectedTrackId) ?? s.project.tracks[0];
      const pat = s.project.patterns.find((p) => p.id === track?.patternId);
      // Anything other than the plain 1:1 chop counts as having a go.
      return !!pat?.steps.some((st, i) => st.slice !== i || st.reverse || st.mute);
    },
  },
  {
    title: 'Now let the presets do it',
    body: 'Fifty-five of them. Try Junglify, or Swap bars 3 & 4. Turn Mix on and they stack instead of replacing, so you can pile up mutes, reverses and rolls. Straight always brings you back to the plain chop.',
    anchor: 'presets',
    view: 'sequence',
    done: () => {
      const s = state();
      const track = s.project.tracks.find((t) => t.id === s.selectedTrackId) ?? s.project.tracks[0];
      const pat = s.project.patterns.find((p) => p.id === track?.patternId);
      return !!pat?.steps.some((st) => st.repeat > 1 || st.pan !== 0 || st.gate < 1 || st.pitch !== 0);
    },
  },
  {
    title: 'That’s the loop',
    body: 'Add more loops and point the same pattern at them, build scenes in Song view, then Export to WAV or stems. Press ? in the top bar any time for the shortcuts.',
    anchor: 'export',
  },
];

const SEEN_KEY = 'wavchop:tour-done:v1';

export function tourWasSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return true;
  }
}

export function Tutorial({ onClose }: { onClose: () => void }) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [done, setDone] = useState<boolean[]>(() => STEPS.map(() => false));
  /** Set when you step back deliberately, so auto-advance stops fighting you. */
  const [paused, setPaused] = useState(false);
  const step = STEPS[index];

  const finish = useCallback(() => {
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      /* private mode — just don't remember */
    }
    onClose();
  }, [onClose]);

  /**
   * Completion latches.
   *
   * Two reasons this isn't just "does the current step's condition hold". Some
   * conditions stop holding — the loop finder's does the moment you close it —
   * so they have to be remembered once seen. And people don't follow
   * instructions in order: load a break from the library instead of opening the
   * finder and the tour has to notice you're already past that step rather than
   * waiting forever for a modal you're never going to open.
   */
  useEffect(() => {
    const id = window.setInterval(() => {
      setDone((prev) => {
        let changed = false;
        const next = prev.map((was, j) => {
          if (was) return true;
          const ok = STEPS[j].done?.() ?? false;
          if (ok) changed = true;
          return ok;
        });
        return changed ? next : prev;
      });
    }, 350);
    return () => clearInterval(id);
  }, []);

  // Follow the user forward, never drag them back.
  useEffect(() => {
    if (paused) return;
    const current = STEPS[index];
    // Steps with nothing to check are things to read — wait for Next.
    if (!current?.done) return;
    if (!done[index]) return;
    // Current one's done, so skip ahead over anything else already satisfied.
    let target = STEPS.length - 1;
    for (let j = index + 1; j < STEPS.length; j++) {
      if (STEPS[j].done && !done[j]) {
        target = j;
        break;
      }
    }
    setIndex((i) => Math.max(i, target));
  }, [done, index, paused]);

  // Put the step's own UI on screen before pointing at it.
  useEffect(() => {
    if (step?.view && state().view !== step.view) state().setUi({ view: step.view });
  }, [step]);

  // Track the anchor's position — panels move, views switch, windows resize.
  useEffect(() => {
    if (!step?.anchor) {
      setRect(null);
      return;
    }
    const measure = () => {
      const el = document.querySelector(`[data-tour="${step.anchor}"]`);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    measure();
    const id = window.setInterval(measure, 250);
    window.addEventListener('resize', measure);
    return () => {
      clearInterval(id);
      window.removeEventListener('resize', measure);
    };
  }, [step]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish]);

  if (!step) return null;
  const last = index === STEPS.length - 1;

  return (
    <>
      {/* Ring only — no backdrop, because you need to keep using the app. */}
      {rect && (
        <div
          className="spotlight fixed z-[60]"
          style={{
            left: rect.left - 5,
            top: rect.top - 5,
            width: rect.width + 10,
            height: rect.height + 10,
          }}
        />
      )}

      <div className="rise fixed bottom-4 left-1/2 z-[61] w-[27rem] -translate-x-1/2 rounded-xl border border-volt-500/50 bg-ink-850/97 p-3.5 shadow-2xl shadow-black/70 backdrop-blur">
        <div className="mb-2 flex items-center gap-2">
          <GraduationCap size={15} className="text-volt-400" />
          <span className="display text-sm">{step.title}</span>
          <button className="btn btn-icon ml-auto" title="End the tour" onClick={finish}>
            <X size={12} />
          </button>
        </div>

        <p className="text-[11px] leading-relaxed text-ink-200">{step.body}</p>
        {step.hint && <p className="mt-1.5 text-[10px] leading-relaxed text-ink-500">{step.hint}</p>}

        <div className="mt-3 flex items-center gap-1.5">
          <div className="flex gap-1">
            {STEPS.map((_, i) => (
              <span
                key={i}
                title={done[i] ? 'Done' : undefined}
                className={clsx(
                  'h-1 rounded-full transition-all duration-300',
                  i === index
                    ? 'w-5 bg-volt-500'
                    : done[i]
                      ? 'w-1.5 bg-volt-600'
                      : 'w-1.5 bg-ink-600',
                )}
              />
            ))}
          </div>

          <span className="mono ml-1 text-[9px] text-ink-500">
            {index + 1}/{STEPS.length}
          </span>

          <button
            className="btn btn-icon ml-auto"
            disabled={index === 0}
            onClick={() => {
              setPaused(true);
              setIndex((i) => Math.max(0, i - 1));
            }}
          >
            <ChevronLeft size={12} />
          </button>
          {last ? (
            <button className="btn btn-primary" onClick={finish}>
              <Check size={12} />
              Done
            </button>
          ) : (
            <button
              className="btn"
              onClick={() => {
                setPaused(false);
                setIndex((i) => i + 1);
              }}
            >
              Next
              <ChevronRight size={12} />
            </button>
          )}
        </div>
      </div>
    </>
  );
}
